// Reading what GAMMIS says about a file we sent.
//
// Three different answers come back depending on what was sent and how far it
// got, and a submitter has to read all three:
//
//   TA1  the envelope was malformed. ISA/GS only. No other acknowledgement
//        follows, so a file that gets one has not been looked at at all.
//   999  the implementation acknowledgement for 270, 276 and 834 files.
//   824  the compliance result for 837 files, within one business day.
//        Georgia validates fee-for-service claims at CLAIM level, so a single
//        bad claim rejects itself and the rest of the file still processes.
//
// A person dropping a downloaded file in should not have to know which is
// which, so `readAcknowledgement` detects the type and returns one shape.

import { parse, findAll, find } from './x12.js';

// ── TA1 ─────────────────────────────────────────────────────────────────────

/** TA105 — what was wrong with the interchange envelope. */
export const TA1_ERRORS = {
  '000': 'No error',
  '001': 'Control number in the header and trailer do not match',
  '004': 'The segment terminator is invalid',
  '005': 'Invalid interchange ID qualifier for sender',
  '006': 'Invalid interchange sender ID',
  '007': 'Invalid interchange ID qualifier for receiver',
  '008': 'Invalid interchange receiver ID',
  '009': 'Unknown interchange receiver ID',
  '013': 'Invalid security information value',
  '014': 'Invalid interchange date value',
  '015': 'Invalid interchange time value',
  '016': 'Invalid interchange standards identifier',
  '017': 'Invalid interchange version ID',
  '018': 'Invalid interchange control number',
  '020': 'Invalid test indicator value',
  '021': 'Invalid number of included groups',
  '022': 'Invalid control structure',
  '023': 'Premature end of file',
  '024': 'Invalid interchange content, for example an invalid GS segment',
  '025': 'Duplicate interchange control number',
  '026': 'Invalid data element separator',
  '027': 'Invalid component element separator',
};

export const parseTA1 = (segments) => {
  const ta1 = find(segments, 'TA1');
  if (!ta1) return null;
  const code = ta1[5] || '';
  return {
    kind: 'TA1',
    accepted: ta1[4] === 'A',
    controlNumber: ta1[1] || null,
    status: ta1[4] || null,
    errorCode: code || null,
    errorText: TA1_ERRORS[code] || (code ? `Interchange error ${code}` : null),
    // An interchange rejected here was never opened. Nothing inside it was
    // looked at, and no 824 or 999 will follow.
    problems: ta1[4] === 'A' ? [] : [TA1_ERRORS[code] || `Interchange rejected (${code || 'no code'})`],
  };
};

// ── 999 ─────────────────────────────────────────────────────────────────────

/** IK501 / AK901 — the verdict on a transaction set or functional group. */
export const ACK_STATUS = {
  A: 'accepted',
  E: 'accepted_with_errors',
  P: 'partially_accepted',
  M: 'rejected',
  R: 'rejected',
  W: 'rejected',
  X: 'rejected',
};

export const parse999 = (segments) => {
  if (!find(segments, 'AK1')) return null;

  const ak1 = find(segments, 'AK1');
  const ak9 = find(segments, 'AK9');
  const sets = [];
  let current = null;

  for (const s of segments) {
    if (s[0] === 'AK2') {
      if (current) sets.push(current);
      current = { transactionType: s[1] || null, controlNumber: s[2] || null, status: null, errors: [] };
      continue;
    }
    if (!current) continue;
    // IK3 names the segment at fault; CTX narrows it to an element.
    if (s[0] === 'IK3') {
      current.errors.push({
        segment: s[1] || null,
        position: s[2] || null,
        loop: s[3] || null,
        code: s[4] || null,
        context: null,
      });
    }
    if (s[0] === 'CTX' && current.errors.length) {
      current.errors[current.errors.length - 1].context = s[1] || null;
    }
    if (s[0] === 'IK4' && current.errors.length) {
      current.errors[current.errors.length - 1].element = s[1] || null;
    }
    if (s[0] === 'IK5') current.status = ACK_STATUS[s[1]] || s[1] || null;
  }
  if (current) sets.push(current);

  const groupStatus = ACK_STATUS[ak9?.[1]] || null;
  return {
    kind: '999',
    accepted: groupStatus === 'accepted',
    respondingTo: { functionalId: ak1?.[1] || null, groupControlNumber: ak1?.[2] || null, version: ak1?.[3] || null },
    status: groupStatus,
    counts: {
      included: Number(ak9?.[2]) || 0,
      received: Number(ak9?.[3]) || 0,
      accepted: Number(ak9?.[4]) || 0,
    },
    transactionSets: sets,
    problems: sets
      .filter((t) => t.status && t.status !== 'accepted')
      .map((t) => `Transaction ${t.controlNumber} ${t.status.replace(/_/g, ' ')}`
        + (t.errors.length
          ? `: ${t.errors.map((e) => `${e.segment}${e.loop ? ` in ${e.loop}` : ''}${e.context ? ` (${e.context})` : ''}`).join(', ')}`
          : '')),
  };
};

// ── 824 ─────────────────────────────────────────────────────────────────────

/** BGN08 — the compliance verdict on an 837 functional group. */
export const APPLICATION_STATUS = {
  WQ: 'accepted',
  U: 'rejected',
  RU: 'partially_accepted',
};

/**
 * External Code Source 895, used in 2100-RED06. The prefix says whether the
 * code is being reported as an error or a warning.
 */
export const APPLICATION_ERRORS = {
  '001': 'Missing or invalid submitter identifier',
  '002': 'Missing or invalid receiver identifier',
  '003': 'Missing or invalid member identifier',
  '004': 'Missing or invalid subscriber identifier',
  '005': 'Missing or invalid patient identifier',
  '011': 'Missing or invalid payer identifier',
  '012': 'Missing or invalid billing provider identifier',
  '013': 'Missing or invalid pay-to provider identifier',
  '014': 'Missing or invalid rendering provider identifier',
  '015': 'Missing or invalid supervising provider identifier',
  '019': 'Missing or invalid referring provider identifier',
  '021': 'Missing or invalid service facility identifier',
  '022': 'Missing or invalid ordering provider identifier',
  '024': 'Amount or quantity out of balance',
  '025': 'Duplicate',
  '026': 'Billing date predates service date',
  '027': 'Business application currently not available',
  '028': 'Sender not authorized for this transaction',
  '029': 'Number of errors exceeds permitted threshold',
  '030': 'Required loop missing',
  '031': 'Required segment missing',
  '032': 'Required element missing',
  '033': 'Situational required loop is missing',
  '034': 'Situational required segment is missing',
  '035': 'Situational required element is missing',
  '036': 'Data too long',
  '037': 'Data too short',
  '038': 'Invalid external code value',
};

/** "E012" / "W031" → { severity, code, text } */
export const readApplicationCode = (raw) => {
  const m = String(raw ?? '').trim().toUpperCase().match(/^([EW])(\d{3})$/);
  if (!m) return { severity: null, code: String(raw ?? '').trim() || null, text: null };
  return {
    severity: m[1] === 'E' ? 'error' : 'warning',
    code: m[2],
    text: APPLICATION_ERRORS[m[2]] || `Application error ${m[2]}`,
  };
};

export const parse824 = (segments) => {
  const bgn = find(segments, 'BGN');
  if (!bgn) return null;

  const status = APPLICATION_STATUS[bgn[8]] || null;
  const problems = [];
  let context = null;

  for (const s of segments) {
    // CTX carries where the error is; RED carries what it is.
    if (s[0] === 'CTX') context = s.slice(1).filter(Boolean).join(' ') || context;
    if (s[0] === 'RED') {
      const code = readApplicationCode(s[6]);
      problems.push({
        ...code,
        where: context,
        description: [code.text, context ? `at ${context}` : null].filter(Boolean).join(' '),
      });
    }
  }

  // OTI carries the reference of the transaction being reported on.
  const otis = findAll(segments, 'OTI').map((s) => ({
    status: s[1] || null,
    referenceQualifier: s[2] || null,
    reference: s[3] || null,
    groupControlNumber: s[8] || null,
    transactionType: s[10] || null,
  }));

  return {
    kind: '824',
    accepted: status === 'accepted',
    status,
    statusCode: bgn[8] || null,
    reference: bgn[2] || null,
    transactions: otis,
    errors: problems,
    problems: problems.filter((p) => p.severity !== 'warning').map((p) => p.description),
  };
};

// ── One door ────────────────────────────────────────────────────────────────

/**
 * Read any acknowledgement GAMMIS sends back.
 *
 * Returns null when the file is not an interchange at all, and
 * `{ kind: 'unknown' }` when it is one but carries none of the three — which
 * is worth saying out loud rather than reporting silence as success.
 */
export const readAcknowledgement = (text) => {
  const { segments } = parse(text);
  if (!segments.length) return null;
  return parseTA1(segments)
    || parse824(segments)
    || parse999(segments)
    || { kind: 'unknown', accepted: false, problems: ['This file is an interchange but carries no TA1, 999 or 824.'] };
};
